import React, { useState } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";

const FlatInsuranceForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    sumInsured: "",
    ownerName: "",
    apartmentAddress: "",
    riskOptions: {
      
      fire: false,
      earthquake: false,
      flood: false,
      burglary: false,
      buildingConditions: false,
      buildingMaintenance: false,
      violence: false,
      diseases: false,
      cyclone: false,
      rsd: false, // Riot & Strike Damage


    },
    price: 0,
  });

  const [openDetails, setOpenDetails] = useState(false);

  const calculatePremium = (data) => {
    const sum = parseFloat(data.sumInsured) || 0;
    const factor = sum / 1000;

    const rates = {
      fire: 0.06,
      earthquake: 0.16,
      flood: 0.17,
      burglary: 1.2,
      buildingConditions: 1.5,
      buildingMaintenance: 2.5,
      violence: 1.95,
      diseases: 1.5,
      cyclone: 0.13,
      rsd: 0.06, // Riot & Strike Damage
    };

    const premiumBreakdown = {
      fire: data.riskOptions.fire ? factor * rates.fire : 0,
      earthquake: data.riskOptions.earthquake ? factor * rates.earthquake : 0,
      flood: data.riskOptions.flood ? factor * rates.flood : 0,
      burglary: data.riskOptions.burglary ? factor * rates.burglary : 0,
      buildingConditions: factor * rates.buildingConditions,
      buildingMaintenance: factor * rates.buildingMaintenance,
      violence: factor * rates.violence,
      diseases: factor * rates.diseases,
      cyclone: data.riskOptions.cyclone ? factor * rates.cyclone : 0,
      rsd: data.riskOptions.rsd ? factor * rates.rsd : 0,
    };

    const netPremium = Object.values(premiumBreakdown).reduce((a, b) => a + b, 0);
    const vat = netPremium * 0.15;
    const totalPremium = Math.round(netPremium + vat);

    return { ...premiumBreakdown, netPremium, vat: Math.round(vat), final: totalPremium };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updated = { ...formData };

    if (type === "checkbox") {
      updated.riskOptions = { ...formData.riskOptions, [name]: checked };
    } else {
      updated[name] = value;
    }

    const calc = calculatePremium(updated);
    updated.price = calc.final;
    setFormData(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const calc = calculatePremium(formData);

    onSubmit({
      productType: "Apartment Owner Insurance",
      data: {
        ...formData,
        ...calc,
      },
    });
  };

  const {
    fire,
    earthquake,
    flood,
    burglary,
    buildingConditions,
    buildingMaintenance,
    violence,
    diseases,
    cyclone,
    rsd,
    netPremium,
    vat,
    final,
  } = calculatePremium(formData);

  return (
    <form onSubmit={handleSubmit}>
      {/* Details Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="outlined" color="secondary" onClick={() => setOpenDetails(true)}>
          Details
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Sum Insured */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Sum Insured (৳)
          </Typography>
          <TextField
            label="Enter sum insured"
            type="number"
            name="sumInsured"
            value={formData.sumInsured}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>

        {/* Owner Name */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Owner Name
          </Typography>
          <TextField
            name="ownerName"
            value={formData.ownerName}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>

        {/* Apartment Address */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Apartment Address
          </Typography>
          <TextField
            name="apartmentAddress"
            value={formData.apartmentAddress}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>

        {/* Optional Coverages */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Optional Coverages
          </Typography>
          {Object.keys(formData.riskOptions).map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  name={key}
                  checked={formData.riskOptions[key]}
                  onChange={handleChange}
                />
              }
              label={key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
            />
          ))}
        </Grid>

        {/* Premium Breakdown */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Premium Breakdown
              </Typography>
              <Typography>Fire: ৳{fire.toFixed(2)}</Typography>
              <Typography>Earthquake: ৳{earthquake.toFixed(2)}</Typography>
              <Typography>Flood: ৳{flood.toFixed(2)}</Typography>
              <Typography>Burglary: ৳{burglary.toFixed(2)}</Typography>
              <Typography>Building Conditions: ৳{buildingConditions.toFixed(2)}</Typography>
              <Typography>Building Maintenance: ৳{buildingMaintenance.toFixed(2)}</Typography>
              <Typography>Violence & Threat: ৳{violence.toFixed(2)}</Typography>
              <Typography>Diseases & Infections: ৳{diseases.toFixed(2)}</Typography>
              <Typography>Cyclone: ৳{cyclone.toFixed(2)}</Typography>
              <Typography>Riot & Strike Damage: ৳{rsd.toFixed(2)}</Typography>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                Net Premium: ৳{netPremium.toFixed(2)}
              </Typography>
              <Typography>VAT (15%): ৳{vat.toFixed(2)}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, color: "green" }}>
                Total Premium: ৳{final.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer Note */}
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
        Note 1: Insurance coverage will start from the next day of current date.
      </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
        Note 2: This rate is only for DSCC and DNCC.
      </Typography>

      {/* Submit */}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{ width: "100%", py: 1.5, fontWeight: 600, fontSize: 16, mt: 2 }}
      >
        Add to Cart
      </Button>

      {/* Details Modal */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Insurance Coverage & Terms</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Coverage Details:</strong>
          </Typography>
          <ul>
            <li>Fire, Earthquake, Flood, Burglary, Cyclone</li>
            <li>Building Conditions & Maintenance</li>
            <li>Riot & Strike Damage (Optional)</li>
            <li>Violence & Threat, Diseases & Infections</li>
          </ul>

          <Typography variant="subtitle1" gutterBottom>
            <strong>Terms & Conditions:</strong>
          </Typography>
          <ul>
            <li>Coverage is valid for one year from the start date.</li>
            <li>Insurance coverage begins from the next day of purchase.</li>
            <li>Excludes deliberate damage, non-disclosed modifications, and illegal activities.</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)} color="primary" variant="contained">
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default FlatInsuranceForm;
